import React, { useEffect, useRef, useState } from "react";
import {
  Edit3,
  Image as ImageIcon,
  IndianRupee,
  MapPin,
  Save,
  Store,
  Tag,
  Trash2,
  X,
} from "lucide-react";

/* =========================================================
   LOCAL DATA HELPERS
========================================================= */

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const cleanRestaurantImage = (value = "") =>
  String(value || "")
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/\\"/g, "")
    .trim();

const RESTAURANT_IMAGE_BASE_URL = "https://bumppy.in/anlook/uploads/";

const getCompleteRestaurantImageUrl = (image = "") => {
  const cleanedImage = cleanRestaurantImage(image);

  if (!cleanedImage) return "";

  if (
    cleanedImage.startsWith("http://") ||
    cleanedImage.startsWith("https://") ||
    cleanedImage.startsWith("data:") ||
    cleanedImage.startsWith("blob:")
  ) {
    return cleanedImage;
  }

  return `${RESTAURANT_IMAGE_BASE_URL}${cleanedImage}`;
};

const getRestaurantImages = (item) => {
  const rawImage =
    item?.restaurant_profile_image ||
    item?.restaurantProfileImage ||
    item?.restaurant_images ||
    item?.restaurantImages ||
    "";

  if (Array.isArray(rawImage)) {
    return rawImage
      .map(cleanRestaurantImage)
      .filter(Boolean)
      .map(getCompleteRestaurantImageUrl);
  }

  if (typeof rawImage !== "string" || !rawImage.trim()) {
    return [];
  }

  const trimmed = rawImage.trim();
  const parsed = safeJsonParse(trimmed);

  if (Array.isArray(parsed)) {
    return parsed
      .map(cleanRestaurantImage)
      .filter(Boolean)
      .map(getCompleteRestaurantImageUrl);
  }

  if (typeof parsed === "string") {
    const imageUrl = getCompleteRestaurantImageUrl(parsed);
    return imageUrl ? [imageUrl] : [];
  }

  return trimmed
    .split(",")
    .map(cleanRestaurantImage)
    .filter(Boolean)
    .map(getCompleteRestaurantImageUrl);
};

const getRestaurantName = (item) =>
  item?.restaurant_name || item?.restaurantName || "Restaurant";

const getRestaurantDescription = (item) =>
  item?.restaurant_description || item?.restaurantDescription || "";

const getRestaurantAddress = (item) =>
  item?.restaurant_address || item?.restaurantAddress || "";

const getRestaurantOffer = (item) =>
  item?.restaurant_offer || item?.restaurantOffer || "";

const getStartingPrice = (item) =>
  item?.starting_price || item?.startingPrice || "";

const getServingType = (item) => item?.serving_type || item?.servingType || "";

const getRestaurantRatings = (item) => item?.ratings || item?.rating || "";

const getCompleteBannerImageUrl = (image = "") => {
  const cleanedImage = cleanRestaurantImage(image);

  if (!cleanedImage) return "";

  if (
    cleanedImage.startsWith("http://") ||
    cleanedImage.startsWith("https://") ||
    cleanedImage.startsWith("data:") ||
    cleanedImage.startsWith("blob:")
  ) {
    return cleanedImage;
  }

  return `${RESTAURANT_IMAGE_BASE_URL}${cleanedImage}`;
};

const getBannerOffer = (item) =>
  item?.banner_type || item?.bannerType || item?.offer || "";

const getBannerImage = (item) => {
  const rawImage =
    item?.banner_image_url ||
    item?.bannerImageUrl ||
    item?.imageUrl ||
    item?.image_url ||
    item?.organization_imageUrl ||
    "";

  return getCompleteBannerImageUrl(rawImage);
};

const getBannerUserCode = (item) =>
  item?.shop_code ||
  item?.shopCode ||
  item?.fkUserId ||
  item?.fk_user_id ||
  item?.userCode ||
  item?.user_code ||
  item?.userId ||
  item?.user_id ||
  item?.userid ||
  "";

const getBannerPlatform = (item) => item?.plateform || item?.platform || "";

const getBannerAspectRatio = (item) =>
  item?.aspect_ratio || item?.aspectRatio || "";

const getAspectRatioStyle = (ratio = "16:9") => {
  const value = String(ratio || "16:9").trim();

  const ratioMap = {
    "1:1": "1 / 1",
    "4:3": "4 / 3",
    "4:5": "4 / 5",
    "16:9": "16 / 9",
  };

  return ratioMap[value] || "16 / 9";
};

/* =========================================================
   CONFIRM DELETE MODAL
========================================================= */

export function ConfirmDeleteModal({
  title,
  message,
  loading,
  onCancel,
  onConfirm,
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start gap-4 border-b border-slate-100 p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
            <Trash2 size={24} />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>

            <p className="mt-1 text-sm leading-6 text-slate-500">{message}</p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-2xl bg-slate-100 p-2 text-slate-500 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={18} />
          </button>
        </div>

        <div className="bg-slate-50 px-5 py-4">
          <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            This action cannot be undone.
          </p>

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 size={17} />

              {loading ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   BANNER CARD
========================================================= */

export function BannerCard({ item, onEdit, onDelete }) {
  const image = getBannerImage(item);

  const [imageError, setImageError] = useState(false);

  const showImage = Boolean(image) && !imageError;

  const userCode = String(
    item?.shop_code ||
      item?.shopCode ||
      item?.user_code ||
      item?.userId ||
      item?.userid ||
      "",
  ).trim();

  const bannerType =
    item?.banner_type || item?.bannerType || item?.offer || "Banner";

  const platform = item?.plateform || item?.platform || "";

  const aspectRatio = item?.aspect_ratio || item?.aspectRatio || "";

  return (
    <div className="relative isolate z-0 flex h-full min-h-[310px] w-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      {/* FIXED HEIGHT IMAGE AREA */}
      <div className="relative isolate h-[180px] w-full shrink-0 overflow-hidden bg-slate-100 sm:h-[190px]">
        {/* TOP BADGES */}
        <div className="absolute left-3 top-3 z-10 flex max-w-[calc(100%-24px)] items-center gap-2">
          {userCode && (
            <span className="max-w-[110px] truncate rounded-full bg-slate-900/80 px-3 py-1 text-[11px] font-bold text-white backdrop-blur-sm">
              {userCode}
            </span>
          )}

          {bannerType && (
            <span className="max-w-[130px] truncate rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold uppercase text-white">
              {bannerType}
            </span>
          )}
        </div>

        {showImage ? (
          <img
            src={image}
            alt={bannerType}
            className="h-full w-full object-center"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-slate-400">
            <ImageIcon size={30} />

            <span className="mt-2 text-xs font-semibold">
              Banner image not found
            </span>
          </div>
        )}
      </div>

      {/* BOTTOM CONTENT */}
      <div className="flex flex-1 flex-col">
        {/* PLATFORM AND RATIO */}
        <div className="flex min-h-[60px] flex-wrap content-center items-center gap-2 px-4 py-3">
          {platform && (
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
              {platform}
            </span>
          )}

          {aspectRatio && (
            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
              {aspectRatio}
            </span>
          )}
        </div>

        {/* BUTTONS ALWAYS AT BOTTOM */}
        <div className="mt-auto grid grid-cols-2 gap-2 px-4 pb-4">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            <Edit3 size={14} />
            Edit
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-sm font-semibold text-red-600 transition hover:bg-red-100"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   COLOR INPUT
========================================================= */

export function ColorInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <div className="flex overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-100">
        <input
          type="color"
          value={value || "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 w-14 cursor-pointer border-0 bg-transparent p-1"
        />

        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ffffff"
          className="w-full bg-transparent px-3 text-sm outline-none"
        />
      </div>
    </label>
  );
}

/* =========================================================
   RESTAURANT CARD
========================================================= */

export function RestaurantCard({ item, onEdit, onDelete }) {
  const images = getRestaurantImages(item);

  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;

    let currentIndex = 0;

    const interval = window.setInterval(() => {
      const slider = sliderRef.current;

      if (!slider) return;

      currentIndex += 1;

      if (currentIndex >= images.length) {
        currentIndex = 0;
      }

      setActiveIndex(currentIndex);

      slider.scrollTo({
        left: slider.clientWidth * currentIndex,
        behavior: "smooth",
      });
    }, 2500);

    return () => window.clearInterval(interval);
  }, [images.length]);

  const handleImageScroll = () => {
    const slider = sliderRef.current;

    if (!slider || images.length <= 1) return;

    const nextIndex = Math.round(slider.scrollLeft / slider.clientWidth);

    setActiveIndex(nextIndex);
  };

  const servingType = getServingType(item);

  const activeStatus = String(
    item?.active_status ?? item?.activeStatus ?? item?.status ?? "Y",
  )
    .trim()
    .toUpperCase();

  const isRestaurantActive = activeStatus !== "N";

  const servingTypeText =
    servingType === "V"
      ? "Veg"
      : servingType === "N"
        ? "Non-Veg"
        : "Veg & Non-Veg";

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="relative h-[145px] w-full overflow-hidden bg-slate-100">
        {images.length > 0 ? (
          <div
            ref={sliderRef}
            onScroll={handleImageScroll}
            className="no-scrollbar flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth"
          >
            {images.map((image, index) => (
              <div
                key={`${image}-${index}`}
                className="h-full min-w-full shrink-0 snap-center"
              >
                <img
                  src={image}
                  alt={`${getRestaurantName(item)} ${index + 1}`}
                  className="block h-full w-full object-cover object-center"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-400">
            <ImageIcon size={30} />
          </div>
        )}

        {/* OFFER */}
        {getRestaurantOffer(item) && (
          <div className="absolute left-3 top-3 z-30 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold text-white shadow">
            {getRestaurantOffer(item)}
          </div>
        )}

        {/* STATUS */}
        <div
          className={`absolute right-3 top-3 z-30 rounded-full px-2.5 py-1 text-[10px] font-bold text-white shadow ${
            isRestaurantActive ? "bg-emerald-600" : "bg-slate-600"
          }`}
        >
          {isRestaurantActive ? "Active" : "Inactive"}
        </div>

        {images.length > 1 && (
          <>

            <div className="absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 gap-1 rounded-full bg-slate-950/40 px-2 py-1">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    activeIndex === index ? "w-3 bg-white" : "w-1.5 bg-white/50"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="space-y-2.5 p-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">
            {getRestaurantName(item)}
          </h3>

          <p className="mt-1 line-clamp-2 text-xs text-slate-500">
            {getRestaurantDescription(item) || "No description added"}
          </p>
        </div>

        <div className="space-y-1.5">
          {getRestaurantAddress(item) && (
            <InfoRow
              icon={<MapPin size={16} />}
              text={getRestaurantAddress(item)}
            />
          )}

          {getRestaurantOffer(item) && (
            <InfoRow icon={<Tag size={16} />} text={getRestaurantOffer(item)} />
          )}

          {getStartingPrice(item) && (
            <InfoRow
              icon={<IndianRupee size={16} />}
              text={`Starting from ₹${getStartingPrice(item)}`}
            />
          )}

          {servingType && (
            <InfoRow icon={<Store size={16} />} text={servingTypeText} />
          )}

          {getRestaurantRatings(item) && (
            <InfoRow
              icon={<Tag size={16} />}
              text={`${getRestaurantRatings(item)} Rating`}
            />
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-slate-100 pt-2.5">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-slate-100 px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            <Edit3 size={16} />
            Edit
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-50 px-2 text-xs font-semibold text-red-600 transition hover:bg-red-100"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   INFO ROW
========================================================= */

export function InfoRow({ icon, text }) {
  return (
    <div className="flex items-start gap-1.5 text-xs text-slate-600">
      <span className="mt-0.5 text-slate-400">{icon}</span>

      <span className="line-clamp-2">{text}</span>
    </div>
  );
}

/* =========================================================
   BASE MODAL
========================================================= */

export function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>

          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="no-scrollbar max-h-[calc(90vh-80px)] overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   INPUT
========================================================= */

export function Input({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  type = "text",
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
      />
    </label>
  );
}

/* =========================================================
   TEXT AREA
========================================================= */

export function TextArea({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <textarea
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
      />
    </label>
  );
}

/* =========================================================
   SINGLE IMAGE UPLOAD
========================================================= */

export function ImageUploadField({
  label,
  imageUrl,
  uploading,
  onUpload,
  onRemove,
}) {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center transition hover:border-emerald-400 hover:bg-emerald-50">
        <ImageIcon size={26} className="mb-2 text-slate-400" />

        <span className="text-sm font-semibold text-slate-700">
          {uploading ? "Uploading image..." : "Click to upload image"}
        </span>

        <span className="mt-1 text-xs text-slate-400">
          PNG, JPG, JPEG supported
        </span>

        <input
          type="file"
          accept="image/*"
          onChange={onUpload}
          disabled={uploading}
          className="hidden"
        />
      </label>

      {imageUrl && (
        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="relative h-40 bg-slate-100">
            <img
              src={imageUrl}
              alt={label}
              className="h-full w-full object-cover"
            />

            <button
              type="button"
              onClick={onRemove}
              className="absolute right-3 top-3 rounded-full bg-red-500 px-3 py-1 text-xs font-bold text-white shadow"
            >
              Remove
            </button>
          </div>

          <div className="truncate px-3 py-2 text-xs text-slate-500">
            {imageUrl}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   MULTIPLE IMAGE UPLOAD
========================================================= */

export function MultipleImageUploadField({
  label,
  imageUrls,
  uploading,
  onUpload,
  onRemoveImage,
}) {
  const images = String(imageUrls || "")
    .split(",")
    .map((image) => image.trim())
    .filter(Boolean);

  return (
    <div>
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center transition hover:border-emerald-400 hover:bg-emerald-50">
        <ImageIcon size={26} className="mb-2 text-slate-400" />

        <span className="text-sm font-semibold text-slate-700">
          {uploading ? "Uploading images..." : "Click to upload images"}
        </span>

        <span className="mt-1 text-xs text-slate-400">
          You can select multiple images
        </span>

        <input
          type="file"
          accept="image/*"
          multiple
          onChange={onUpload}
          disabled={uploading}
          className="hidden"
        />
      </label>

      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((image, index) => (
            <div
              key={`${image}-${index}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
            >
              <div className="relative h-28 bg-slate-100">
                <img
                  src={image}
                  alt={`${label} ${index + 1}`}
                  className="h-full w-full object-cover"
                />

                <button
                  type="button"
                  onClick={() => onRemoveImage(index)}
                  className="absolute right-2 top-2 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white shadow"
                >
                  X
                </button>
              </div>

              <div className="truncate px-2 py-1.5 text-[10px] text-slate-500">
                {image}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   MODAL ACTIONS
========================================================= */

export function ModalActions({ loading, onCancel, submitText }) {
  return (
    <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="rounded-2xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Cancel
      </button>

      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Save size={17} />

        {loading ? "Saving..." : submitText}
      </button>
    </div>
  );
}

/* =========================================================
   EMPTY STATE
========================================================= */

export function EmptyState({ icon, title, text }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
        {icon}
      </div>

      <h3 className="text-base font-bold text-slate-900">{title}</h3>

      <p className="mt-1 max-w-sm text-sm text-slate-500">{text}</p>
    </div>
  );
}

/* =========================================================
   RESTAURANT SKELETON
========================================================= */

export function RestaurantSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-3xl border border-slate-200 bg-white"
        >
          <div className="h-44 animate-pulse bg-slate-100" />

          <div className="space-y-3 p-4">
            <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-100" />
            <div className="h-4 w-full animate-pulse rounded-full bg-slate-100" />
            <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-100" />

            <div className="mt-4 flex gap-2">
              <div className="h-10 flex-1 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-10 flex-1 animate-pulse rounded-2xl bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
